import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageTitle, Card, Button } from '../components/common';
import BASE_URL from '../services/baseURL';
import { getImageUrl } from '../services/imagePathConfig';

interface ProfileData {
    name: string;
    email: string;
    mobileNumber: string;
    description: string;
    location: string;
    about: string;
    skills: string[];
    linkedin: string;
    github: string;
    projects: { projectName: string; projectDescription: string; githubLink: string }[];
    experiences: { id: number; title: string; company: string; duration: string; description: string; relatedSkills: string }[];
    education: { id: number; school: string; degree: string; fieldOfStudy: string; duration: string }[];
    profilePicture: string | null;
}

const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

const avatarColors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'];
const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
};

const AlumniDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (id) fetchProfile(id);
    }, [id]);

    const fetchProfile = async (traineeToken: string) => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.post(`${BASE_URL}/trainee/getTraineeProfile`, { TraineeToken: traineeToken });
            const data = res.data.data;

            let parsedProjects = [];
            if (data.profileDetail?.projects) {
                if (Array.isArray(data.profileDetail.projects)) {
                    parsedProjects = data.profileDetail.projects;
                } else if (typeof data.profileDetail.projects === 'string') {
                    try {
                        parsedProjects = JSON.parse(data.profileDetail.projects);
                    } catch (e) {
                        console.error('Failed to parse projects:', e);
                        parsedProjects = [];
                    }
                }
            }

            let parsedSkills = [];
            if (data.profileDetail?.skills) {
                if (Array.isArray(data.profileDetail.skills)) {
                    parsedSkills = data.profileDetail.skills;
                } else if (typeof data.profileDetail.skills === 'string') {
                    try {
                        parsedSkills = JSON.parse(data.profileDetail.skills);
                    } catch (e) {
                        console.error('Failed to parse skills:', e);
                        parsedSkills = [];
                    }
                }
            }

            // Use the profilePicture field returned by the backend and construct full URL
            const profilePictureUrl = getImageUrl(data.profileDetail?.profilePicture) || null;

            console.log('Profile Picture URL:', profilePictureUrl);
            console.log('Parsed Skills:', parsedSkills);
            console.log('Parsed Projects:', parsedProjects);

            setProfile({
                name: data.basicInfo?.fullName || '',
                email: data.basicInfo?.email || '',
                mobileNumber: data.basicInfo?.mobileNumber || '',
                description: data.profileDetail?.description || '',
                location: data.profileDetail?.location || '',
                about: data.profileDetail?.about || '',
                skills: parsedSkills,
                linkedin: data.profileDetail?.linkedin || '',
                github: data.profileDetail?.github || '',
                projects: parsedProjects,
                experiences: data.experiences || [],
                education: data.education || [],
                profilePicture: profilePictureUrl,
            });
        } catch (err: any) {
            console.error('Error fetching profile:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-700">{error || 'Alumni not found'}</h2>
                <Button className="mt-4" onClick={() => navigate('/alumni')}>Back to Alumni</Button>
            </div>
        );
    }

    return (
        <div>
            <PageTitle title="Alumni Details" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Left Column */}
                <div className="lg:col-span-1 flex flex-col gap-6">
                    {/* Profile Card */}
                    <Card className="p-6">
                        <div className="flex flex-col items-center text-center">
                            {profile.profilePicture ? (
                                <img src={profile.profilePicture} alt={profile.name} className="w-32 h-32 rounded-full border-4 border-red-100 shadow-md object-cover" />
                            ) : (
                                <div className={`w-32 h-32 rounded-full flex items-center justify-center border-4 border-red-100 shadow-md ${getAvatarColor(profile.name)}`}>
                                    <span className="text-white text-4xl font-bold">{getInitials(profile.name)}</span>
                                </div>
                            )}
                            <h2 className="mt-4 text-xl font-bold text-gray-900">{profile.name}</h2>
                            <p className="mt-2 text-sm text-red-500 font-medium">{profile.description || '-'}</p>
                            <div className="flex items-center gap-1 mt-2 text-gray-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                <span className="text-sm">{profile.location || '-'}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-2 text-gray-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                <span className="text-sm">{profile.email}</span>
                            </div>
                        </div>
                    </Card>

                    {/* Skills Card */}
                    {Array.isArray(profile.skills) && profile.skills.length > 0 && (
                        <Card className="p-6">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Skills</h3>
                            <div className="flex flex-wrap gap-2">
                                {profile.skills.map((skill, index) => (
                                    <span key={index} className="px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-600 border border-red-100">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Social Links Card */}
                    {(profile.linkedin || profile.github || (Array.isArray(profile.projects) && profile.projects.some(p => p.githubLink))) && (
                        <Card className="p-6">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Social Links</h3>
                            <div className="flex gap-3 justify-center">
                                {profile.linkedin && (
                                    <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                        <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                    </a>
                                )}
                                {profile.github && (
                                    <a href={profile.github} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                                        <svg className="w-6 h-6 text-gray-800" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
                                    </a>
                                )}
                                {Array.isArray(profile.projects) && profile.projects.filter(p => p.githubLink).slice(0, 1).map((project, idx) => (
                                    <a key={idx} href={project.githubLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" title={project.projectName}>
                                        <svg className="w-6 h-6 text-gray-800" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
                                    </a>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Education Card */}
                    {profile.education.length > 0 && (
                        <Card className="p-6">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Education</h3>
                            <div className="space-y-3">
                                {profile.education.map((edu) => (
                                    <div key={edu.id} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                                        <h4 className="font-semibold text-sm text-gray-800">{edu.degree}{edu.fieldOfStudy ? ` in ${edu.fieldOfStudy}` : ''}</h4>
                                        <p className="text-xs text-gray-500 mt-1">{edu.school}</p>
                                        {edu.duration && <p className="text-xs text-gray-400 mt-0.5">{edu.duration}</p>}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </div>

                {/* Right Column */}
                <div className="lg:col-span-2 flex flex-col gap-6">
                    {/* About Section */}
                    {profile.about && (
                        <Card className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">About</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">{profile.about}</p>
                        </Card>
                    )}

                    {/* Projects Section */}
                    {Array.isArray(profile.projects) && profile.projects.length > 0 && (
                        <Card className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Projects</h3>
                            <div className="space-y-4">
                                {profile.projects.map((project, index) => (
                                    <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                                        <h4 className="font-semibold text-gray-800">{project.projectName}</h4>
                                        {project.projectDescription && <p className="text-sm text-gray-500 mt-1">{project.projectDescription}</p>}
                                        {project.githubLink && (
                                            <a href={project.githubLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
                                                GitHub
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Experience Section */}
                    {profile.experiences.length > 0 && (
                        <Card className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Experience</h3>
                            <div className="space-y-4">
                                {profile.experiences.map((exp) => (
                                    <div key={exp.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                                        <h4 className="font-semibold text-gray-800">{exp.title}</h4>
                                        <p className="text-sm text-gray-500 mt-1">{exp.company}</p>
                                        {exp.duration && <p className="text-xs text-gray-400 mt-1">{exp.duration}</p>}
                                        {exp.description && <p className="text-sm text-gray-600 mt-2">{exp.description}</p>}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Empty state if no content */}
                    {!profile.about && profile.projects.length === 0 && profile.experiences.length === 0 && (
                        <Card className="p-6">
                            <p className="text-sm text-gray-500 text-center py-8">No additional details available for this alumni.</p>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AlumniDetailPage;
